import React from "react";
import { useParams } from "react-router-dom";

// components
import SearchAndBrowseIconGridContainer from "icons/SearchAndBrowseIconGridContainer";
import NotFound from "core/NotFound";
import SectionContainer from "core/SectionContainer";
import IconCollectionDetailHero from "../IconCollectionDetailHero";
import Skeleton from "core/Skeleton";

// queries
import { GET_ICONS_IN_COLLECTION } from "icons/BrowseIconsInCollection/queries";

// helpers
import useLoadMore from "frontend/hooks/useLoadMore";
import { useCurrentUserContext } from "contexts/CurrentUserContext";

// styles
import PageMeta from "core/PageMeta";

const BrowseIconsInCollection = () => {
    const limit = 200;
    const { collectionSlugWithId } = useParams();
    const collectionId = collectionSlugWithId.split("-").pop();

    const {
        currentUser: { data: userData }
    } = useCurrentUserContext();
    const { currentUser } = userData;

    const {
        items = [],
        totalCount,
        loadingFirstBatch,
        loading,
        LoadMore,
        loadingItemCount,
        data
    } = useLoadMore({
        limit: limit,
        query: GET_ICONS_IN_COLLECTION,
        queryVariables: {
            collectionId,
            limit
        },
        pathToListKey: "collectionDetail.icons.items",
        pathToTotalCountKey: "collectionDetail.icons.totalCount",
        contentType: "icon"
    });

    if (data && !loading && !data.collectionDetail) {
        return <NotFound />;
    }

    const creator = data?.collectionDetail?.creator || {};
    const collectionName = data?.collectionDetail?.name || "";
    const {
        avatarUrl: creatorAvatarUrl = "",
        name: creatorName = "",
        username: creatorUsername = "",
        country: creatorCountry = ""
    } = creator;

    // NOTE:
    // These values also exist in templates/frontend/browse_icon_collections.html
    const pageMetaTitle = `${collectionName} Icon Collection by ${creatorName} | Noun Project`;
    const pageMetaDescription = `Explore the ${collectionName} icon collection by ${creatorName} at Noun Project. Browse these icons and millions more today!`;

    return (
        <>
            <PageMeta
                title={pageMetaTitle}
                description={pageMetaDescription}
                indexContent="index,follow,max-image-preview:large"
            />

            <IconCollectionDetailHero
                collectionId={collectionId}
                collectionName={collectionName}
                creatorName={creatorName}
                creatorCountry={creatorCountry}
                creatorUsername={creatorUsername}
                creatorAvatarUrl={creatorAvatarUrl}
                totalIconCount={totalCount}
                iconTitleAndThumbnailList={items.map(item => ({
                    title: item.title,
                    thumbnail: item.thumbnails.thumbnail200
                }))}
                // TODO: currently hardcoding "team" because we're temporarily only showing the "nonupgradable subscriber" version of the hero.  make dynamic when we're ready to show other versions.
                userSubscriptionType="team"
                showCollectionFortLink={currentUser.isSuperuser}
                loading={!data} // passing `!data` instead of `loading` to the `loading` prop here because passing `loading` was resulting in the `NotFound` component flashing before the page loaded
            />

            <SectionContainer topSpacing="lg" bottomSpacing="3xl">
                <SearchAndBrowseIconGridContainer
                    iconData={items}
                    totalCount={totalCount}
                    loadingFirstBatch={loadingFirstBatch}
                    loadingMore={loading}
                    LoadMoreButton={LoadMore}
                    loadingItemCount={loadingItemCount}
                />
            </SectionContainer>
        </>
    );
};

export default BrowseIconsInCollection;
